import { getCanonicalUrl } from "@/lib/seo";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const DEFAULT_IMAGE = "https://www.howisthe.surf/og-image.png";
const STRUCTURED_DATA_ID = "page-structured-data";

interface SeoProps {
  title: string;
  description: string;
  canonicalPath?: string;
  image?: string;
  structuredData?: object;
}

function ensureMetaByName(name: string): HTMLMetaElement {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  return el;
}

function ensureMetaByProperty(property: string): HTMLMetaElement {
  let el = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  return el;
}

function ensureCanonical(): HTMLLinkElement {
  let el = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  return el;
}

export default function Seo({
  title,
  description,
  canonicalPath,
  image = DEFAULT_IMAGE,
  structuredData,
}: SeoProps) {
  const location = useLocation();
  const path = canonicalPath ?? location.pathname;
  const canonicalUrl = getCanonicalUrl(path);
  const structuredDataJson = structuredData ? JSON.stringify(structuredData) : "";

  useEffect(() => {
    document.title = title;

    ensureMetaByName("description").setAttribute("content", description);
    ensureMetaByProperty("og:title").setAttribute("content", title);
    ensureMetaByProperty("og:description").setAttribute("content", description);
    ensureMetaByProperty("og:url").setAttribute("content", canonicalUrl);
    ensureMetaByProperty("og:image").setAttribute("content", image);
    ensureMetaByName("twitter:title").setAttribute("content", title);
    ensureMetaByName("twitter:description").setAttribute("content", description);
    ensureMetaByName("twitter:image").setAttribute("content", image);
    ensureCanonical().setAttribute("href", canonicalUrl);

    const existingStructuredData = document.getElementById(STRUCTURED_DATA_ID);
    if (!structuredDataJson) {
      existingStructuredData?.remove();
      return;
    }

    const script = existingStructuredData ?? document.createElement("script");
    script.id = STRUCTURED_DATA_ID;
    script.setAttribute("type", "application/ld+json");
    script.textContent = structuredDataJson;

    if (!existingStructuredData) {
      document.head.appendChild(script);
    }
  }, [canonicalUrl, description, image, structuredDataJson, title]);

  return null;
}
